import { ReactNode } from "react";
import { INode } from "../../@types/device";
import { ServiceContextType } from "../../@types/service";
import { Widget } from "../../enum";
import {
  asArray,
  gaugeCfg,
  pct,
  rawValue,
  readValue,
  rgbKeys,
  sliderCfg,
  widgetOf,
} from "../../lib/node";

/**
 * The one-line value a dashboard card shows for a device. Same dispatch as
 * DeviceWidget, small output -- this replaces the parallel if/else ladder
 * DeviceCard used to carry.
 */
export function summaryOf(node: INode, ctx: ServiceContextType): ReactNode {
  switch (widgetOf(node)) {
    case Widget.READOUT: {
      const reads = asArray(node.reads);
      if (!reads.length) return null;
      return reads
        .map((r) => `${readValue(ctx, r, node.addr) ?? "--"}${r.unit ?? ""}`)
        .join(" ");
    }

    case Widget.GAUGE: {
      const { min, max } = gaugeCfg(node);
      const raw = rawValue(ctx, node.addr, node.stateKey);
      return raw === undefined ? "--" : `${Math.round(pct(raw, min, max))}%`;
    }

    case Widget.SLIDER: {
      const { min, max } = sliderCfg(node);
      const raw = rawValue(ctx, node.addr, node.stateKey);
      return raw === undefined ? "--" : `${Math.round(pct(raw, min, max))}%`;
    }

    case Widget.BUTTONS: {
      const current = rawValue(ctx, node.addr, node.stateKey);
      return asArray(node.buttons).find((b) => b.value === current)?.label ?? "--";
    }

    case Widget.COLOR: {
      const k = rgbKeys(node);
      const rgb = [k.r, k.g, k.b].map((x) => rawValue(ctx, node.addr, x) ?? 0);
      return (
        <span
          className="inline-block w-4 h-4 rounded-md border border-white/20"
          style={{ backgroundColor: `rgb(${rgb.join(",")})` }}
        />
      );
    }

    case Widget.AC: {
      const target = node.ac?.targetKey
        ? rawValue(ctx, node.addr, node.ac.targetKey)
        : undefined;
      const fan = node.ac?.fanKey
        ? rawValue(ctx, node.addr, node.ac.fanKey)
        : undefined;
      return `${target ?? "--"}°C | ${fan ?? "--"}`;
    }

    default:
      return null;
  }
}
